import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import LandingPageExitMenu from '../../Navigation/LandingPageExitMenu';
import LandingHeroIconNav, {
	FITNESS_HUB_NAV_ITEMS
} from '../../Navigation/LandingHeroIconNav';
import getLandingImageHeight from '../../../utils/getLandingImageHeight';
import FitnessHubImage from '../../../../assets/images/fitness_hub.jpg';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import { sortFitnessProfilesForDisplay } from '../../../utils/sortFitnessProfilesForDisplay';
import FitnessProfilePrimaryBadge from '../../FitnessProfile/shared/FitnessProfilePrimaryBadge';
import useChallengeWireSummary from '../../../hooks/useChallengeWireSummary';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';
import { trainerBriefingBadgeCount } from '../../../utils/landingPageChallengeBadge';

const hubIconClassName =
	'bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md';

export default function FitnessHubPage() {
	const { user, endpoints, csrfToken } = useAuth();
	const { guardSubscription } = useSubscriptionGate();
	const summary = useChallengeWireSummary(user, endpoints, csrfToken);
	const trainerBadge = trainerBriefingBadgeCount(summary);
	const [isProfilesModalOpen, setIsProfilesModalOpen] = useState(false);
	const [profiles, setProfiles] = useState([]);
	const [profilesLoading, setProfilesLoading] = useState(false);
	const [profilesError, setProfilesError] = useState('');

	useEffect(() => {
		if (!isProfilesModalOpen) return;

		if (!user?.id || !endpoints?.get_user_fitness_profiles_endpoint) {
			setProfiles([]);
			setProfilesError('');
			setProfilesLoading(false);
			return;
		}

		let cancelled = false;

		async function loadProfiles() {
			setProfilesLoading(true);
			setProfilesError('');

			try {
				const data = await fetchUserFitnessProfiles({
					userId: user.id,
					endpoint: endpoints.get_user_fitness_profiles_endpoint,
					csrfToken
				});
				if (!cancelled) setProfiles(sortFitnessProfilesForDisplay(data));
			} catch (error) {
				if (!cancelled) {
					setProfiles([]);
					setProfilesError(error.message || 'Could not load fitness profiles.');
				}
			} finally {
				if (!cancelled) setProfilesLoading(false);
			}
		}

		loadProfiles();

		return () => {
			cancelled = true;
		};
	}, [csrfToken, endpoints, isProfilesModalOpen, user?.id]);

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="mt-4">
				<PageTitle title="Fitness Hub" />
			</div>

			<div className="relative mt-4 rounded-lg overflow-hidden shadow-lg">
				<img
					src={FitnessHubImage}
					alt="Fitness Hub"
					decoding="async"
					className="block w-full object-cover object-center"
					style={getLandingImageHeight()}
				/>

				<LandingHeroIconNav
					items={FITNESS_HUB_NAV_ITEMS}
					summary={summary}
					exitMenuCurrentPage="fitness-hub"
					exitTriggerTitle="Leave Fitness Hub"
					iconClassName="bg-blue-500"
					exitMenuContainerClassName="relative w-12 h-12 md:hidden"
					exitTriggerClassName={`w-full h-full flex items-center justify-center ${hubIconClassName}`}
					onProfilesClick={() => setIsProfilesModalOpen(true)}
				>
					<Link
						to="/ai-trainer"
						className={`relative w-12 h-12 flex items-center justify-center ${hubIconClassName}`}
						title="AI Trainer"
					>
						🤖
						{trainerBadge > 0 && (
							<span className="absolute -top-1 -right-1 flex h-4 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-normal leading-none text-white">
								{trainerBadge > 9 ? '9+' : trainerBadge}
							</span>
						)}
					</Link>
					<div
						className={`w-12 h-12 flex items-center justify-center ${hubIconClassName}`}
						onClick={() =>
							window.open(
								'https://www.muscleandstrength.com/exercises',
								'_blank',
								'noopener,noreferrer'
							)
						}
						title="Exercises"
					>
						💪
					</div>
				</LandingHeroIconNav>

				<div className="hidden md:block absolute bottom-4 right-4 z-10">
					<LandingPageExitMenu
						currentPage="fitness-hub"
						summary={summary}
						triggerTitle="Leave Fitness Hub"
					/>
				</div>
			</div>

			{isProfilesModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-75 px-4">
					<div className="w-full max-w-4xl rounded-lg bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border p-5 shadow-xl text-gray-900 dark:text-dark-text">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Fitness Profiles</h3>
							<button
								type="button"
								onClick={() => setIsProfilesModalOpen(false)}
								className="text-xl font-semibold leading-none text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text"
								aria-label="Close Fitness Profiles"
							>
								×
							</button>
						</div>

						{profilesLoading ? (
							<p className="mt-4 text-sm text-gray-500 dark:text-dark-text-secondary">Loading profiles…</p>
						) : !user?.id ? (
							<p className="mt-4 text-sm text-gray-600 dark:text-dark-text-secondary">
								Sign in to view your fitness profiles.
							</p>
						) : profilesError ? (
							<p className="mt-4 text-sm text-red-600 dark:text-red-400">{profilesError}</p>
						) : profiles.length === 0 ? (
							<p className="mt-4 text-sm text-gray-600 dark:text-dark-text-secondary">
								No fitness profiles yet. Create one to get started.
							</p>
						) : (
							<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
								{profiles.map((profile) => (
									<Link
										key={profile.id}
										to={`/fitness-profiles/${profile.id}`}
										className="group rounded-md border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
										onClick={() => setIsProfilesModalOpen(false)}
									>
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-semibold text-gray-900 dark:text-dark-text">{profile.name}</span>
											{profile.is_primary && <FitnessProfilePrimaryBadge />}
										</div>
									</Link>
								))}
							</div>
						)}

						<div className="mt-4 flex justify-end gap-4">
							<Link
								to="/fitness-profiles/new"
								onClick={(event) => {
									if (!guardSubscription()) {
										event.preventDefault();
										return;
									}
									setIsProfilesModalOpen(false);
								}}
								className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								Create profile
							</Link>
							<Link
								to="/fitness-profiles"
								onClick={() => setIsProfilesModalOpen(false)}
								className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								View all profiles
							</Link>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
